import { httpsCallable, type HttpsCallable, type HttpsCallableResult } from "firebase/functions";
import { getFirebase } from "./firebase";

/** Wire shape of the consolidated backend callable (functions/src/api/admin/admin-api.ts). */
interface AdminEnvelope { action: string; payload: unknown }
export type AdminCall<TIn = unknown, TOut = unknown> = (data?: TIn) => Promise<HttpsCallableResult<TOut>>;

let handle: HttpsCallable<AdminEnvelope, unknown> | null = null;
/** Test seam. */
export function _resetAdminCallHandle(): void { handle = null; }

/**
 * Every admin endpoint is ONE Cloud Function, `adminApi`, so a session pays
 * one cold start, not one per page; `name` is the action it dispatches on (the
 * pre-28/09/2026 per-endpoint callable name — keep in lock-step with ADMIN_ACTIONS).
 */
export function call<TIn = unknown, TOut = unknown>(name: string): AdminCall<TIn, TOut> {
  return async (data) => {
    handle ??= httpsCallable<AdminEnvelope, unknown>(getFirebase().functions, "adminApi");
    return (await handle({ action: name, payload: data ?? {} })) as HttpsCallableResult<TOut>;
  };
}

/**
 * Typed wrappers over admin-tier callable Cloud Functions.
 * Each wrapper is added as the corresponding CF lands in functions/src/api/admin/.
 * All admin CFs are gated by requireAdminRole and emit audit log entries.
 */
export const adminApi = {
  // Phase 2
  getKpis: () => call<void, AdminKpis>("adminGetKpis"),

  // Phase 3 — users
  listUsers: () => call<ListUsersInput, ListUsersOutput>("adminListUsers"),
  getUser: () => call<{ uid: string }, AdminUserDetail>("adminGetUser"),
  suspendUser: () => call<{ uid: string; reason?: string }, { ok: true }>("adminSuspendUser"),
  restoreUser: () => call<{ uid: string }, { ok: true }>("adminRestoreUser"),
  resetDateOfBirth: () => call<{ uid: string }, { ok: true }>("adminResetDateOfBirth"),
  impersonateUser: () =>
    call<{ uid: string }, { customToken: string; expiresAt: number }>("adminImpersonateUser"),
  requestAccountDeletion: () =>
    call<
      { uid: string; reason?: string },
      { ok: true; scheduledDeletionAt: number }
    >("adminRequestAccountDeletion"),
  sendPasswordReset: () => call<{ uid: string }, { ok: true }>("adminSendPasswordReset"),
  setRole: () =>
    call<{ uid: string; role: "platform_admin" | "end_user" }, { ok: true }>("adminSetRole"),

  // Phase 4 — schools (Phase 4: reference data)
  listSchools: () =>
    call<ListSchoolsInput, ListSchoolsOutput>("adminListSchools"),
  bulkImportSchools: () =>
    call<BulkImportSchoolsInput, BulkImportSchoolsOutput>("adminBulkImportSchools"),
  getSchool: () =>
    call<{ id: string }, AdminSchoolDetail>("adminGetSchool"),
  updateSchool: () =>
    call<{ id: string; patch: UpdateSchoolPatch }, { ok: true }>("adminUpdateSchool"),
  setSchoolActive: () =>
    call<{ id: string; isActive: boolean }, { ok: true; isActive: boolean }>("adminSetSchoolActive"),

  // Phase 4 — charities
  listCharities: () =>
    call<ListCharitiesInput, ListCharitiesOutput>("adminListCharities"),
  getCharity: () =>
    call<{ id: string }, AdminCharityDetail>("adminGetCharity"),
  updateCharity: () =>
    call<{ id: string; patch: UpdateCharityPatch }, { ok: true }>("adminUpdateCharity"),
  setCharityVerified: () =>
    call<
      { id: string; isVerified: boolean; reason?: string },
      { ok: true; isVerified: boolean }
    >("adminSetCharityVerified"),
  setCharityActive: () =>
    call<
      { id: string; isActive: boolean },
      { ok: true; isActive: boolean }
    >("adminSetCharityActive"),

  // Phase 4 — categories (system + custom triage)
  listCategories: () => call<void, ListCategoriesOutput>("adminListCategories"),
  updateCategory: () =>
    call<{ id: string; patch: UpdateCategoryPatch }, { ok: true }>("adminUpdateCategory"),
  setCategoryActive: () =>
    call<
      { id: string; isActive: boolean },
      { ok: true; isActive: boolean }
    >("adminSetCategoryActive"),
  listCustomCategories: () =>
    call<ListCustomCategoriesInput, ListCustomCategoriesOutput>(
      "adminListCustomCategories",
    ),
  triageCustomCategory: () =>
    call<TriageCustomInput, { ok: true; resultCategoryId: string | null }>(
      "adminTriageCustomCategory",
    ),

  // Phase 8 — audit log
  queryAuditLogs: () =>
    call<QueryAuditLogsInput, QueryAuditLogsOutput>("adminQueryAuditLogs"),

  // Diagnostics
  parcel2GoHealth: () =>
    call<Record<string, never>, Parcel2GoHealthReport>("adminParcel2GoHealth"),

  // Postal / carrier operations
  listOpsAlerts: () =>
    call<ListOpsAlertsInput, ListOpsAlertsOutput>("adminListOpsAlerts"),
  resolveOpsAlerts: () =>
    call<{ alertIds: string[]; note?: string }, { resolved: number; skipped: number }>(
      "adminResolveOpsAlerts",
    ),
  listPostalReservations: () =>
    call<ListPostalReservationsInput, ListPostalReservationsOutput>(
      "adminListPostalReservations",
    ),
  getPostalReservation: () =>
    call<{ id: string }, { id: string; reservation: Record<string, unknown> }>(
      "adminGetPostalReservation",
    ),
  listReturns: () => call<ListReturnsInput, ListReturnsOutput>("adminListReturns"),

  // Referrals (review queue) + support overrides
  listReferrals: () => call<ListReferralsInput, ListReferralsOutput>("adminListReferrals"),
  decideReferral: () => call<DecideReferralInput, AdminReferralRow>("adminDecideReferral"),
  clearRemovalClock: () => call<{ uid: string }, { cleared: boolean }>("adminClearRemovalClock"),

  // Pricing configuration (postal handling fee)
  getPricingConfig: () => call<void, PricingConfigOutput>("adminGetPricingConfig"),
  updatePricingConfig: () =>
    call<UpdatePricingConfigInput, PricingConfigOutput>("adminUpdatePricingConfig"),
  getReturn: () => call<{ id: string }, GetReturnOutput>("adminGetReturn"),

  // Commerce — tag orders and items
  listTagOrders: () => call<ListTagOrdersInput, ListTagOrdersOutput>("adminListTagOrders"),
  listItems: () => call<ListItemsInput, ListItemsOutput>("adminListItems"),

  // Launch waitlist
  listLaunchSubscribers: () =>
    call<ListLaunchSubscribersInput, ListLaunchSubscribersOutput>("adminListLaunchSubscribers"),
  sendLaunchEmail: () =>
    call<{ subscriberIds: string[] }, LaunchSendOutput>("adminSendLaunchEmail"),
  sendReminderEmail: () =>
    call<{ subscriberIds: string[]; customMessage?: string }, LaunchSendOutput>("adminSendReminderEmail"),
  markUnsubscribed: () =>
    call<{ subscriberIds: string[] }, { updated: number; skipped: number }>("adminMarkUnsubscribed"),

  // Phase 5 — tags
  generateTagBatch: () =>
    call<GenerateTagBatchInput, GenerateTagBatchOutput>("adminGenerateTagBatch"),
  markBatchReceived: () =>
    call<MarkBatchReceivedInput, MarkBatchReceivedOutput>("adminMarkBatchReceived"),
  listTagBatches: () =>
    call<ListTagBatchesInput, ListTagBatchesOutput>("adminListTagBatches"),
  listPoolTags: () =>
    call<ListPoolTagsInput, ListPoolTagsOutput>("adminListPoolTags"),
  getPoolTag: () => call<{ tagId: string }, PoolTagDetail>("adminGetPoolTag"),
  exportBatch: () =>
    call<{ batchId: string }, ExportBatchOutput>("adminExportBatch"),
  revokeBatch: () =>
    call<{ batchId: string; reason?: string; force?: boolean }, RevokeBatchOutput>("adminRevokeBatch"),
  grantTagCredits: () =>
    call<GrantTagCreditsInput, GrantTagCreditsOutput>("adminGrantTagCredits"),
  setStripeMode: () =>
    call<SetStripeModeInput, SetStripeModeOutput>("adminSetStripeMode"),

  // Promo codes
  createPromoCodes: () =>
    call<CreatePromoCodesInput, CreatePromoCodesOutput>("adminCreatePromoCodes"),
  listPromoCodes: () =>
    call<ListPromoCodesInput, ListPromoCodesOutput>("adminListPromoCodes"),
  getPromoCode: () => call<{ code: string }, GetPromoCodeOutput>("adminGetPromoCode"),
  updatePromoCode: () =>
    call<UpdatePromoCodeInput, UpdatePromoCodeOutput>("adminUpdatePromoCode"),

  // Catalogue (inventory/ + Stripe price rotation)
  listCatalog: () => call<void, ListCatalogOutput>("adminListCatalog"),
  setCatalogPrice: () =>
    call<SetCatalogPriceInput, SetCatalogPriceOutput>("adminSetCatalogPrice"),
  updateCatalogDisplay: () =>
    call<UpdateCatalogDisplayInput, CatalogProduct>("adminUpdateCatalogDisplay"),
  syncCatalog: () => call<void, SyncCatalogOutput>("adminSyncCatalog"),

  // Return analytics
  exportReturnAnalytics: () =>
    call<ExportReturnAnalyticsInput, ExportReturnAnalyticsOutput>("adminExportReturnAnalytics"),
};

// ── Phase 5 — tags types ─────────────────────────────────────────────────────

/** Manufactured tags — the only kinds that are bought, credited and batched. Mirrors functions/utils/tag-id-gen.ts. */
export type PhysicalTagType = "qr_sticker" | "qr_label" | "nfc";
/** `digital`: a printable tag the app generates for an account (signup tag / subscriber slots). */
export type TagType = PhysicalTagType | "digital";
/** Human noun per tag type — mirrors TAG_TYPE_LABEL in functions/services/tag-claim-service.ts. */
export const TAG_TYPE_LABEL: Record<TagType, string> = { qr_sticker: "sticker", qr_label: "label", nfc: "keyring", digital: "digital" };
/** Capitalised noun in the owner-facing alias ("Sticker #1") — mirrors functions/src/utils/tag-alias.ts. */
export const TAG_TYPE_NOUN: Record<TagType, string> = { qr_sticker: "Sticker", qr_label: "Label", nfc: "Keyring", digital: "Digital" };
export function tagAliasNoun(type: string): string {
  return (TAG_TYPE_NOUN as Record<string, string>)[type] ?? "Tag";
}
/** `paused`: a subscriber-generated tag whose owner's subscription lapsed. */
export type TagPoolStatus = "inventory" | "unassigned" | "assigned" | "paused" | "revoked";
export type TagOrigin = "physical" | "generated";
export type TagEntitlement = "signup" | "subscription" | "referral";
export const TAG_TYPES: PhysicalTagType[] = ["qr_sticker", "qr_label", "nfc"];
/** Available credits per physical type (`usage/{uid}.tagCredits`), missing → 0. */
export type TagCreditMap = Record<PhysicalTagType, number>;

/** Mirrors functions/api/admin/grant-tag-credits.ts: signed, non-zero, |credits| ≤ 500. */
export interface GrantTagCreditsInput {
  uid: string;
  type: PhysicalTagType;
  credits: number;
  reason: string;
}
export interface GrantTagCreditsOutput {
  uid: string;
  type: PhysicalTagType;
  requested: number;
  /** What the balance actually moved by (a removal clamps at 0). */
  applied: number;
  balance: number;
  credits: TagCreditMap;
}
export const MAX_CREDITS_PER_GRANT = 500;
export const MAX_REASON_LENGTH = 500;

/**
 * Mirrors functions/api/admin/set-stripe-mode.ts. "test" puts ONE account's
 * new tag orders and postal holds on the Stripe sandbox (and past the
 * pre-launch store gate); "live" removes the flag. Server-owned
 * `users/{uid}.stripeMode`.
 */
export type StripeMode = "live" | "test";
export interface SetStripeModeInput {
  uid: string;
  mode: StripeMode;
}
export interface SetStripeModeOutput {
  uid: string;
  mode: StripeMode;
  previousMode: StripeMode;
}

export interface GenerateTagBatchInput {
  count: number;
  type: PhysicalTagType;
  notes?: string;
}
export interface GenerateTagBatchOutput {
  batchId: string;
  count: number;
  type: PhysicalTagType;
  tagIds: string[];
}

/** Mirrors functions/src/services/tag-claim-service.ts. */
export type ClaimPolicy = "credit" | "free";

/** Mirrors functions/src/api/admin/mark-batch-received.ts. */
export interface MarkBatchReceivedInput {
  batchId: string;
  claimPolicy?: ClaimPolicy;
}
export interface MarkBatchReceivedOutput {
  batchId: string;
  /** Tags moved from `inventory` to `unassigned` by THIS call (0 on a re-run). */
  received: number;
  receivedAtMillis: number;
  claimPolicy: ClaimPolicy;
}

export interface ListTagBatchesInput {
  limit?: number;
  cursor?: string | null;
}
export interface TagBatchRow {
  batchId: string;
  count: number;
  type: string;
  generatedAtMillis: number;
  generatedBy: string;
  generatedByEmail: string | null;
  exportedAtMillis: number | null;
  notes: string | null;
  statusCounts: {
    inventory: number;
    unassigned: number;
    assigned: number;
    revoked: number;
  };
}
export interface ListTagBatchesOutput {
  batches: TagBatchRow[];
  nextCursor: string | null;
}

export interface ListPoolTagsInput {
  status?: TagPoolStatus;
  type?: TagType;
  origin?: TagOrigin;
  batchId?: string;
  userId?: string;
  /** Always sent together with `status: "revoked"` (the backend index is (status, revokedReason)). */
  revokedReason?: string;
  limit?: number;
  cursor?: string | null;
}
export interface PoolTagRow {
  tagId: string;
  status: string;
  type: string;
  origin: TagOrigin;
  entitlement: TagEntitlement | null;
  batchId: string;
  generatedAtMillis: number;
  userId: string | null;
  /** The owner's per-type alias number (null until first owned). */
  aliasSeq: number | null;
  claimedAtMillis: number | null;
  itemId: string | null;
  assignedAtMillis: number | null;
  revokedAtMillis: number | null;
  revokedReason: string | null;
  /** Why a paused tag is paused: `lapsed` | `expired`. */
  pausedReason: string | null;
}
export interface ListPoolTagsOutput {
  tags: PoolTagRow[];
  nextCursor: string | null;
}

export interface PoolTagDetail extends PoolTagRow {
  generatedBy: string;
  ownerEmail: string | null;
  ownerName: string | null;
  /** The name the owner sees in the app ("Yile_Sticker-1"); the app never shows the id. */
  alias: string | null;
  itemName: string | null;
  releasedAtMillis: number | null;
}

export interface ManifestRow {
  tagId: string;
  url: string;
  type: string;
  status: string;
}
export interface ExportBatchOutput {
  batchId: string;
  rows: ManifestRow[];
}
export interface RevokeBatchOutput {
  batchId: string;
  revoked: number;
  skipped: number;
}

export type LaunchSubscriberStatus = "pending" | "notified" | "unsubscribed";

export interface ListLaunchSubscribersInput {
  search?: string;
  status?: LaunchSubscriberStatus;
  subscribedAfter?: string;
  subscribedBefore?: string;
  limit?: number;
  cursor?: string | null;
}

export interface LaunchSubscriberRow {
  id: string;
  email: string;
  status: LaunchSubscriberStatus;
  source: string;
  subscribedAt: number | null;
  notifiedAt: number | null;
  reminderSentAt: number | null;
  unsubscribedAt: number | null;
  lastResubmitAt: number | null;
}

export interface ListLaunchSubscribersOutput {
  subscribers: LaunchSubscriberRow[];
  nextCursor: string | null;
  /** First page only; null on later pages. */
  totals: { pending: number; notified: number; unsubscribed: number; all: number } | null;
}

export interface LaunchSendOutput {
  sent: number;
  skipped: number;
  failed: number;
  errors: { id: string; reason: string }[];
}

// Mirrors CheckResult / HealthReport in functions/src/api/admin/parcel2go-health.ts
export interface Parcel2GoCheckResult {
  ok: boolean;
  ms?: number;
  detail?: unknown;
  error?: string;
}

export interface Parcel2GoHealthReport {
  ok: boolean;
  auth: Parcel2GoCheckResult;
  dropshops: Parcel2GoCheckResult;
  prepay: Parcel2GoCheckResult;
  opsAlerts: Parcel2GoCheckResult;
  ranAt: string;
}

// Postal / carrier operations — mirrors functions/src/api/admin/list-ops-alerts.ts,
// list-postal-reservations.ts, resolve-ops-alerts.ts, get-postal-reservation.ts.
export interface ListOpsAlertsInput {
  status?: "open" | "resolved";
  limit?: number;
  cursor?: string | null;
}

export interface OpsAlertRow {
  id: string;
  type: string;
  createdAt: number | null;
  resolvedAt: number | null;
  resolvedBy: string | null;
  resolutionNote: string | null;
  data: Record<string, unknown>;
}

export interface ListOpsAlertsOutput {
  alerts: OpsAlertRow[];
  nextCursor: string | null;
  /** First page only; null on later pages. Both span ALL statuses regardless of the filter. */
  totalCount: number | null;
  openCount: number | null;
}

export interface ListPostalReservationsInput {
  status?: string;
  limit?: number;
  cursor?: string | null;
}

export interface PostalReservationRow {
  id: string;
  reservationId: string;
  returnId: string;
  itemId: string;
  carrier: string | null;
  status: string;
  dropOffNetwork: string | null;
  servicePointName: string | null;
  trackingNumber: string | null;
  qrPending: boolean;
  priceExVat: number | null;
  remindersSent: number;
  reservedAt: number | null;
  expiresAt: number | null;
  createdAt: number | null;
  updatedAt: number | null;
}

export interface ListPostalReservationsOutput {
  reservations: PostalReservationRow[];
  nextCursor: string | null;
  /** First page only; null on later pages. */
  totalCount: number | null;
}

// Returns — mirrors functions/src/api/admin/list-returns.ts, get-return.ts,
// and (for the state vocabulary) functions/src/utils/return-states.ts. The
// single source of truth for `ReturnState` on the client: `returns.tsx` and
// `status-tone.ts` import both `RETURN_STATES` and `ReturnState` from here.
export const RETURN_STATES = [
  "awaiting_collection",
  "pending_owner_acceptance",
  "awaiting_payment",
  "awaiting_finder_service_point_selection",
  "creating_reservation",
  "awaiting_dropoff",
  "dropped_off",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "completed",
  "declined",
  "failed",
  "expired",
] as const;
export type ReturnState = (typeof RETURN_STATES)[number];

export interface ListReturnsInput {
  state?: ReturnState | "";
  limit?: number;
  cursor?: string | null;
}

export interface ReturnRow {
  id: string;
  returnId: string;
  itemId: string;
  itemName: string | null;
  ownerUid: string;
  finderUid: string | null;
  isAnonymousReport: boolean;
  returnMethod: string;
  state: ReturnState;
  finderIntent: string | null;
  finderLocationLabel: string | null;
  handedInTo: string | null;
  proposedSchoolName: string | null;
  createdAt: number | null;
  updatedAt: number | null;
}

export interface ListReturnsOutput {
  returns: ReturnRow[];
  nextCursor: string | null;
  /** First page only; null on later pages. */
  totalCount: number | null;
}

export interface GetReturnOutput {
  id: string;
  /** `returnRequests/{id}` — the owner-facing mirror; null if it never existed. */
  request: Record<string, unknown> | null;
  /** `returns/{id}` — the finder's original report, `finderContact` masked; null if it never existed. */
  report: Record<string, unknown> | null;
}

export interface ListTagOrdersInput {
  status?: string;
  limit?: number;
  cursor?: string | null;
}

export interface TagOrderRow {
  id: string;
  userId: string | null;
  status: string;
  packageName: string | null;
  quantity: number | null;
  amountPence: number | null;
  currency: string;
  /** Typed credits the paid order granted (`[{ type, count }]`), empty until paid. */
  creditLines: Array<{ type: PhysicalTagType; count: number }>;
  creditsGranted: number;
  creditsRevoked: number;
  creditsGrantedAtMillis: number | null;
  createdAtMillis: number | null;
  paidAtMillis: number | null;
}

export interface ListTagOrdersOutput {
  orders: TagOrderRow[];
  nextCursor: string | null;
}

export interface ListItemsInput {
  userId: string;
  limit?: number;
  cursor?: string | null;
}

export interface AdminItemRow {
  id: string;
  name: string;
  status: string;
  category: string;
  createdAt: number | null;
}

export interface ListItemsOutput {
  items: AdminItemRow[];
  nextCursor: string | null;
}

// ---- Type stubs (filled in fully when each phase implements its CF) ----

export interface AdminKpis {
  /**
   * Where the money figures came from. "counter" is the denormalised
   * adminCounters/kpi_commerce doc; "scan" means the backfill has not stamped
   * it yet, so the endpoint still reads every paid order and charity per poll.
   */
  revenueSource: "counter" | "scan";
  activeUsers: number;
  signupsToday: number;
  signupsThisWeek: number;
  itemsRegistered: number;
  tagsActivated: number;
  claimsSubmitted: number;
  postalReturnsInFlight: number;
  charityDonationsTotalPence: number;
  refundRatePct: number;
  feeRevenuePence: number;
  signupsByDay: { date: string; count: number }[];
  itemsByStatus: { status: string; count: number }[];
  /** In-flight return stages only — every one of these can still move. */
  returnsFunnel: { state: string; count: number }[];
  /** Terminal return states. Cumulative totals, not stages: they only grow. */
  returnOutcomes: { state: string; count: number }[];
}

export interface ListUsersInput {
  search?: string;
  status?: "active" | "disabled";
  role?: "platform_admin" | "end_user";
  schoolUrn?: string;
  /** Server-side: dateOfBirth missing, under 18, or 18+. */
  ageBand?: "missing" | "under18" | "adult";
  signupAfter?: string;
  signupBefore?: string;
  limit?: number;
  cursor?: string | null;
}

export interface ListUsersOutput {
  users: AdminUserRow[];
  nextCursor: string | null;
}

export interface QueryAuditLogsInput {
  eventType?: string;
  severity?: "info" | "warning" | "error" | "critical";
  actorType?: "end_user" | "platform_admin" | "service_account" | "system";
  userId?: string;
  startDate?: number;
  endDate?: number;
  limit?: number;
  cursor?: number | null;
}

export interface QueryAuditLogsOutput {
  logs: AuditLogRow[];
  nextCursor: number | null;
}

export interface AuditLogRow {
  id: string;
  eventType: string;
  severity: "info" | "warning" | "error" | "critical";
  actorType: "end_user" | "platform_admin" | "service_account" | "system";
  timestamp: number;
  userId?: string;
  ipAddress?: string;
  userAgent?: string;
  resource?: string;
  action?: string;
  result: "success" | "failure";
  metadata?: Record<string, unknown>;
  errorMessage?: string;
}

export interface AdminUserRow {
  uid: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  authSource?: string;
  schoolName?: string;
  schoolUrn?: string;
  subscriptionTier?: string;
  subscriptionPlatform?: SubscriptionPlatform | null;
  role?: string;
  disabled?: boolean;
  createdAt: number;
  dateOfBirth?: number | null;
  itemsCreated: number;
}

// Phase 4 — schools

export interface ListSchoolsInput {
  search?: string;
  status?: "active" | "inactive";
  postcodePrefix?: string;
  sortBy?: "name" | "recent" | "labelCount";
  limit?: number;
  cursor?: string | null;
}

export interface AdminSchoolRow {
  id: string;
  name: string;
  postcode: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  website: string | null;
  isActive: boolean;
  acceptsDropoffs: boolean;
  labelCount: number;
  lastUpdated: string | null;
}

export interface ListSchoolsOutput {
  schools: AdminSchoolRow[];
  nextCursor: string | null;
  totalMatching: number;
}

export interface SchoolImportRow {
  /** 20-char Firestore doc-key — used for direct upsert (bundled-style). */
  id?: string;
  /** UK Department for Education URN — dedupe key for refresh imports. */
  urn?: string;
  name: string;
  contact?: { person?: string; email?: string; phone?: string };
  location?: { street?: string; address?: string; postcode?: string };
  website?: string;
  logoUrl?: string;
}

export interface BulkImportSchoolsInput {
  schools: SchoolImportRow[];
  /** When true, returns the per-row create/update preview without writing. */
  dryRun?: boolean;
}

export interface BulkImportSchoolsError {
  index: number;
  reason: string;
}

export interface BulkImportSchoolsChange {
  index: number;
  intent: "create" | "update";
  docId: string;
  urn?: string;
  name: string;
}

export interface BulkImportSchoolsOutput {
  dryRun: boolean;
  summary: {
    received: number;
    created: number;
    updated: number;
    errors: BulkImportSchoolsError[];
  };
  preview: BulkImportSchoolsChange[];
}

export interface AdminSchoolDetail {
  id: string;
  name: string;
  urn: string | null;
  contact: { person?: string; email?: string; phone?: string } | null;
  location: { street?: string; address?: string; postcode?: string } | null;
  website: string | null;
  logoUrl: string | null;
  status: { isActive: boolean; labelCount: number; lastUpdated: string | null };
  acceptsDropoffs: boolean;
  dropoffInstructions: string | null;
  metadata: {
    source: string | null;
    createdAt: number | null;
    updatedAt: number | null;
    createdBy: string | null;
    lastModifiedBy: string | null;
  };
  registeredUsers: number;
}

/** Pass `null` to clear an optional field (urn / website / logoUrl / dropoffInstructions). */
export interface UpdateSchoolPatch {
  name?: string;
  urn?: string | null;
  contact?: { person?: string; email?: string; phone?: string };
  location?: { street?: string; address?: string; postcode?: string };
  website?: string | null;
  logoUrl?: string | null;
  acceptsDropoffs?: boolean;
  dropoffInstructions?: string | null;
}

// Phase 4 — charities

export interface ListCharitiesInput {
  search?: string;
  status?: "active" | "inactive";
  verified?: "verified" | "unverified";
  sortBy?: "name" | "recent" | "donations";
  limit?: number;
  cursor?: string | null;
}

export interface AdminCharityRow {
  id: string;
  name: string;
  email: string | null;
  website: string | null;
  isActive: boolean;
  isVerified: boolean;
  totalDonations: number;
  totalAmount: number;
  itemsHelped: number;
  updatedAt: number | null;
}

export interface ListCharitiesOutput {
  charities: AdminCharityRow[];
  nextCursor: string | null;
  totalMatching: number;
}

export interface AdminCharityDetail {
  id: string;
  name: string;
  description: string;
  isVerified: boolean;
  isActive: boolean;
  taxId: string | null;
  email: string | null;
  website: string | null;
  phone: string | null;
  address: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  } | null;
  logoUrl: string | null;
  bannerUrl: string | null;
  stats: {
    totalDonations: number;
    totalAmount: number;
    itemsHelped: number;
    donationsCount: number;
  };
  createdAt: number | null;
  updatedAt: number | null;
}

/** Pass `null` to clear an optional field. */
export interface UpdateCharityPatch {
  name?: string;
  description?: string;
  taxId?: string | null;
  email?: string;
  website?: string | null;
  phone?: string | null;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  logoUrl?: string | null;
  bannerUrl?: string | null;
}

// Phase 4 — categories

export interface AdminCategoryRow {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  version: string | null;
  isSystemCategory: boolean;
  isDeleted: boolean;
  subcategoryCount: number;
  itemCount: number;
  updatedAt: number | null;
}

export interface ListCategoriesOutput {
  categories: AdminCategoryRow[];
  total: number;
}

export interface UpdateCategoryPatch {
  name?: string;
  description?: string | null;
  icon?: string | null;
  version?: string | null;
}

export interface ListCustomCategoriesInput {
  search?: string;
  sortBy?: "popular" | "recent" | "name";
  limit?: number;
  cursor?: string | null;
}

export interface AdminCustomCategoryRow {
  id: string;
  name: string;
  nameLower: string;
  createdBy: string | null;
  usageCount: number;
  createdAt: number | null;
  lastUsedAt: number | null;
}

export interface ListCustomCategoriesOutput {
  customCategories: AdminCustomCategoryRow[];
  nextCursor: string | null;
  totalMatching: number;
}

export interface TriageCustomInput {
  id: string;
  action: "promote_to_system" | "merge" | "reject";
  mergeIntoSystemId?: string;
  newSubcategoryKey?: string;
  reason?: string;
}

export interface AdminUserDetail {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  phoneNumber: string | null;
  photoURL: string | null;
  disabled: boolean;
  providerIds: string[];
  creationTime: number | null;
  lastSignInTime: number | null;
  lastRefreshTime: number | null;
  customClaims: Record<string, unknown> | null;
  doc: Record<string, unknown> | null;
  itemsCreated: number;
  pendingClaims: number;
  scheduledDeletionAt: number | null;
  tagCredits: TagCreditMap;
  /** The free signup tag (`usage/{uid}.signupTagId`), null until verified. */
  signupTagId: string | null;
  /** Subscriber-generated tag slots used and the cap. */
  generatedTags: number;
  generatedTagLimit: number;
  /** UTC epoch millis the 7-day digital-tag removal clock runs out; null when none runs. */
  digitalTagRemovalDueAt: number | null;
  /** Return opened by the account's first paused find (once-per-account courtesy). */
  pausedCourtesyReturnId: string | null;
}

// Promo codes — mirrors functions/src/api/admin/{create,list,get,update}-promo-code(s).ts
export type SubscriptionPlatform = "apple" | "google" | "promo";

export interface CreatePromoCodesInput {
  /** Generated mode only (mutually exclusive with `customCode`); 1–500, default 1. */
  count?: number;
  /** Generated mode only; 2–12 chars, A–Z/0–9. */
  prefix?: string;
  /** Custom mode only (mutually exclusive with `count`/`prefix`). */
  customCode?: string;
  /** Exactly one of `grantDays` / `grantUntil` must be sent. */
  grantDays?: number;
  /** ISO 8601 UTC. */
  grantUntil?: string;
  maxRedemptions?: number | null;
  /** ISO 8601; null/omitted = never expires. */
  expiresAt?: string | null;
  requiresSchoolUrn?: string | null;
  campaign: string;
  note?: string;
}
export interface CreatePromoCodesOutput {
  codes: string[];
}

export interface ListPromoCodesInput {
  status?: "active" | "disabled";
  campaign?: string;
  limit?: number;
  cursor?: string | null;
}
export interface PromoCodeRow {
  code: string;
  campaign: string;
  note: string | null;
  grantDays: number | null;
  /** Epoch millis (UTC). */
  grantUntil: number | null;
  maxRedemptions: number | null;
  redemptionCount: number;
  expiresAt: number | null;
  requiresSchoolUrn: string | null;
  status: "active" | "disabled";
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}
export interface ListPromoCodesOutput {
  items: PromoCodeRow[];
  nextCursor: string | null;
}

/** "240 days" / "until 31/08/2027" — the grant a redemption earns. */
export function formatPromoGrant(row: PromoCodeRow, formatDate: (millis: number) => string): string {
  if (row.grantDays != null) return `${row.grantDays} days`;
  return row.grantUntil != null ? `until ${formatDate(row.grantUntil)}` : "—";
}

/** "3 / 300" or "3 / ∞". */
export function formatPromoRedeemed(row: PromoCodeRow): string {
  return `${row.redemptionCount} / ${row.maxRedemptions ?? "∞"}`;
}

export interface PromoCodeRedemptionRow {
  userId: string;
  redeemedAt: number;
  grantedUntil: number;
}
export interface GetPromoCodeOutput {
  code: PromoCodeRow;
  redemptions: PromoCodeRedemptionRow[];
}

export interface UpdatePromoCodeInput {
  code: string;
  status?: "active" | "disabled";
  note?: string;
}
export interface UpdatePromoCodeOutput {
  code: PromoCodeRow;
}

// Return analytics — mirrors functions/src/api/admin/export-return-analytics.ts
export interface ExportReturnAnalyticsInput {
  /** ISO 8601. */
  from?: string;
  /** ISO 8601. */
  to?: string;
}
export interface ExportReturnAnalyticsOutput {
  rows: Record<string, unknown>[];
}

// Pricing config — mirrors functions/src/api/admin/pricing-config.ts.
export interface PricingConfigOutput {
  /** PinStaQ fee (£) added on top of the live carrier quote. */
  postalHandlingFee: number;
  postalReturnCurrency: string;
  /** Fair-use ceiling on a Premium account's generated digital tags (1..200). */
  generatedTagLimit: number;
  referralHoldDays: number;
  referralMaxPerReferrer24h: number;
  referralMaxPerIp24h: number;
  referralSignupWindowHours: number;
  updatedAt: number | null;
  updatedBy: string | null;
}
/** At least one field is required. */
export interface UpdatePricingConfigInput {
  postalHandlingFee?: number;
  generatedTagLimit?: number;
  referralHoldDays?: number;
  referralMaxPerReferrer24h?: number;
  referralMaxPerIp24h?: number;
  referralSignupWindowHours?: number;
}

// Referrals — mirrors functions/src/api/admin/{list-referrals,decide-referral}.ts.
// The server never sends installHash / ipHash / phoneHash.
export type ReferralStatus =
  | "registered" | "verified" | "review" | "credited" | "awarded" | "rejected" | "clawed_back";
export type ReferralDecision = "approve" | "reject" | "clawback";
export interface AdminReferralRow {
  refereeUid: string;
  referrerUid: string;
  status: ReferralStatus;
  signals: string[];
  reviewReason: string | null;
  rejectReason: string | null;
  reviewNote: string | null;
  reviewedBy: string | null;
  tagId: string | null;
  capped: boolean;
  /** UTC epoch millis. */
  createdAt: number | null;
  verifiedAt: number | null;
  eligibleAt: number | null;
  awardedAt: number | null;
  resolvedAt: number | null;
  reviewedAt: number | null;
  clawedBackAt: number | null;
}
export interface ListReferralsInput {
  status?: ReferralStatus;
  referrerUid?: string;
  refereeUid?: string;
  limit?: number;
  cursor?: string | null;
}
export interface ListReferralsOutput {
  referrals: AdminReferralRow[];
  nextCursor: string | null;
}
export interface DecideReferralInput {
  refereeUid: string;
  decision: ReferralDecision;
  note?: string;
}

// ── Catalogue types (mirror functions/src/services/catalog-service.ts + api/admin/*-catalog*.ts) ──

export interface CatalogProduct {
  sku: string;
  type: "sticker" | "label" | "keyring";
  name: string;
  description: string;
  units: number;
  pricePence: number;
  packLabel: string;
  isActive: boolean;
  isPopular: boolean;
  sortOrder: number;
  stripeProductId: string | null;
  stripePriceId: string | null;
  /** Epoch millis, or null when the doc has no timestamp. */
  updatedAt: number | null;
}

export interface ListCatalogOutput {
  products: CatalogProduct[];
  stripeMode: "live" | "test";
  /** UTC ISO-8601 read time. */
  asOf: string;
}

export interface SetCatalogPriceInput {
  sku: string;
  pricePence: number;
  reason?: string;
}

export interface SetCatalogPriceOutput {
  product: CatalogProduct;
  warning?: "old_price_not_archived";
}

export interface UpdateCatalogDisplayInput {
  sku: string;
  isPopular?: boolean;
  sortOrder?: number;
}

export interface SyncCatalogOutput {
  upserted: string[];
  deactivated: string[];
  skipped: number;
}
